import React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import HomeIcon from './icons/HomeIcon';
import UsersIcon from './icons/UsersIcon';
import BriefcaseIcon from './icons/BriefcaseIcon';
import SparklesIcon from './icons/SparklesIcon';
import SearchIcon from './icons/SearchIcon';
import VerifiedBadge from './VerifiedBadge';
import LogoutIcon from './icons/LogoutIcon';
import VolumeUpIcon from './icons/VolumeUpIcon';
import PhotoIcon from './icons/PhotoIcon';
import ListBulletIcon from './icons/ListBulletIcon';
import SettingsIcon from './icons/SettingsIcon';
import MicrophoneIcon from './icons/MicrophoneIcon';

const LeftSidebar: React.FC = () => {
  const { currentUser, managedProfile, logout } = useAuth();
  const { handleNavigation, view } = useNavigation();
  const { opportunities } = useOpportunities();
  const { t } = useLanguage();
  
  const displayUser = managedProfile || currentUser;

  if (!displayUser) {
    return (
      <aside className="h-full flex flex-col space-y-6">
        <div className="bg-brand-surface rounded-lg p-4 animate-pulse">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-gray-700 rounded-full"></div>
            <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-700 rounded w-3/4"></div>
                <div className="h-2 bg-gray-700 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </aside>
    );
  }

  const tier = displayUser.subscriptionTier || 'basic';
  const isPro = tier.includes('_pro');
  const isProPlus = tier.includes('_pro_plus');
  const canAccessLineups = ['talent_pro', 'talent_pro_plus', 'business_pro', 'business_pro_plus'].includes(tier);
  const isSeed = currentUser?.isSeed;
  const isAdmin = !!currentUser?.isAdmin;
  const showProDashboard = isProPlus || isSeed || isAdmin;

  const hasPostedOpportunities = opportunities.some(opp => opp.author.handle === displayUser.handle);

  return (
    <aside className="h-full flex flex-col space-y-4 font-urbanist">
      <div className="flex-shrink-0 bg-brand-surface rounded-lg p-4 flex items-center space-x-3 border border-gray-800 shadow-sm z-10">
        <div 
            className="flex-1 flex items-center space-x-3 cursor-pointer"
            onClick={() => handleNavigation('profile', { user: displayUser })}
        >
            <div className="relative flex-shrink-0">
                <img
                    src={displayUser.avatarUrl}
                    alt={displayUser.name}
                    className="h-10 w-10 rounded-full border-2 border-brand-orange shadow-sm"
                />
                {isProPlus ? (
                    <VerifiedBadge className="absolute -bottom-1 -right-1 h-4 w-4 text-blue-500 bg-brand-surface rounded-full p-0.5" title="Verified Pro+ User" />
                ) : isPro ? (
                    <VerifiedBadge className="absolute -bottom-1 -right-1 h-4 w-4 text-green-400 bg-brand-surface rounded-full p-0.5" title="Pro User" />
                ) : null}
            </div>
            <div className="flex-1 overflow-hidden">
                <h2 className="text-sm font-bold text-white truncate tracking-tight">{displayUser.name}</h2>
                <p className="text-[10px] text-brand-muted truncate font-mono">@{displayUser.handle}</p>
            </div>
        </div>
        <button
            onClick={() => handleNavigation('createOpportunity')}
            aria-label={t('NAV_POST_OPP')}
            className="flex-shrink-0 h-11 w-11 rounded-full shadow-lg shadow-brand-orange/30 active:scale-95 transition-all bg-brand-orange flex items-center justify-center p-2"
        >
            <img src="/icon.png" alt="Post opportunity" className="w-full h-full object-contain" />
        </button>
      </div>
      
      <nav className="flex-1 min-h-0 bg-brand-surface rounded-lg p-3 border border-gray-800 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1">
          <NavItem icon={<HomeIcon className="h-5 w-5" />} label={t('NAV_HOME')} active={view === 'feed'} onClick={() => handleNavigation('feed')} />
          <NavItem icon={<SearchIcon className="h-5 w-5" />} label="Search" active={view === 'search'} onClick={() => handleNavigation('search')} />
          <NavItem icon={<MicrophoneIcon className="h-5 w-5" />} label="Sessions" active={view === 'sessions'} onClick={() => handleNavigation('sessions')} />
          <NavItem icon={<SparklesIcon className="h-5 w-5" />} label={t('NAV_FOR_YOU')} active={view === 'recommended'} onClick={() => handleNavigation('recommended')} />

          {isAdmin && (
             <NavItem icon={<SettingsIcon className="h-5 w-5 text-red-400" />} label="Admin Dashboard" active={view === 'adminDashboard'} onClick={() => handleNavigation('adminDashboard')} />
          )}
          {showProDashboard && (
             <NavItem icon={<UsersIcon className="h-5 w-5 text-blue-400" />} label={isSeed ? "Founder's Hub" : "Pro Analytics"} active={view === 'proDashboard'} onClick={() => handleNavigation('proDashboard')} />
          )}
          
          <NavItem icon={<UsersIcon className="h-5 w-5" />} label={t('NAV_MY_BANDS')} active={view === 'myProjects'} onClick={() => handleNavigation('myProjects')} />

          <NavItem icon={<UsersIcon className="h-5 w-5" />} label={t('NAV_NETWORK')} active={view === 'network'} onClick={() => handleNavigation('network')} />
          <NavItem icon={<BriefcaseIcon className="h-5 w-5" />} label={t('NAV_ACTIVITY')} active={view === 'myActivity'} onClick={() => handleNavigation('myActivity')} />
          {hasPostedOpportunities && (
            <NavItem icon={<BriefcaseIcon className="h-5 w-5" />} label={t('NAV_MANAGE_APPLICANTS')} active={view === 'manageApplicants'} onClick={() => handleNavigation('manageApplicants')} />
          )}
          {canAccessLineups && (
            <>
              <div className="pt-2 pb-1 px-3 text-[10px] font-extrabold text-brand-muted tracking-[0.2em] uppercase">Management</div>
              <NavItem icon={<ListBulletIcon className="h-5 w-5" />} label={t('NAV_MY_LINEUPS')} active={view === 'myLineups'} onClick={() => handleNavigation('myLineups')} />
              <NavItem icon={<PhotoIcon className="h-5 w-5" />} label={t('NAV_MY_FLYERS')} active={view === 'myFlyers'} onClick={() => handleNavigation('myFlyers')} />
            </>
          )}

          <div className="pt-2 border-t border-gray-800 mt-2">
            <NavItem icon={<VolumeUpIcon className="h-5 w-5" />} label={t('NAV_PUMP_UP')} active={view === 'pumpUpTheVolume'} onClick={() => handleNavigation('pumpUpTheVolume')} />
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-800 mt-4 pt-3">
            <NavItem icon={<LogoutIcon className="h-5 w-5" />} label={t('LOGOUT')} onClick={logout} />
        </div>
      </nav>
    </aside>
  );
};

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center p-2.5 w-full text-left rounded-lg transition-all duration-200 ${
      active
        ? 'text-white bg-brand-orange/20 font-bold'
        : 'text-brand-muted hover:text-white hover:bg-gray-800'
    }`}
  >
    <div className={`${active ? 'text-brand-orange' : ''}`}>{icon}</div>
    <span className="ml-3 text-sm">{label}</span>
  </button>
);

export default LeftSidebar;